<template>
  <div v-if="createStore.document">
    <div v-if="!hideTitle" class="mt-6 text-grey-darken-4 font-weight-bold">
      {{ i18n.t('titles.who_needs_to_sign') }}
    </div>
    <InviteeSettings>
      <!-- SIGNEES OR APPROVERS -->
      <div
        v-show="createStore.hasInvitees"
        class="overflow-auto"
        :class="{
          'is-group': createStore.signeesOrdered.some(
            (group) => group.length > 1
          )
        }"
      >
        <v-form
          ref="signaturesForm"
          data-test-id="signing-form"
          @submit.prevent
        >
          <UnorderedInvitees
            v-if="!createStore.ordered"
            @on-blur="onBlur"
            @on-focus="onFocus"
            @on-update="handleSigneeDebounced"
            @on-update-key-down="handleSigneeDebounced"
            @remove-invitee="removeInviteeFromDocument"
          />
          <OrderedInvitees
            v-else
            @on-blur="onBlur"
            @on-focus="onFocus"
            @on-update="handleSigneeDebounced"
            @on-update-key-down="handleSigneeDebounced"
            @remove-invitee="removeInviteeFromDocument"
          />
        </v-form>
      </div>
      <!-- SIGNEES OR APPROVERS -->

      <div
        class="d-flex ga-4 mt-4 mb-6 justify-space-between"
        :class="{
          'mt-6': dragging,
          'flex-wrap': display.mobile.value && display.xs.value
        }"
      >
        <!-- ADD SIGNEE -->
        <v-btn
          :class="
            display.mobile.value && display.xs.value
              ? 'w-100'
              : 'flex-grow-1 flex-shrink-1'
          "
          color="primary"
          data-test-id="add-signee"
          variant="outlined"
          @click="addSigneeField"
        >
          {{ i18n.t('buttons.add_signatory') }}
        </v-btn>
        <!-- /ADD SIGNEE -->
        <!-- ADD APPROVER -->
        <v-btn
          v-if="expertSettings || layoutStableExpert"
          class="add-approver-button"
          :class="[
            display.mobile.value && display.xs.value
              ? 'w-100'
              : 'flex-grow-1 flex-shrink-1',
            {
              'expert-setting--visually-hidden':
                layoutStableExpert && !expertSettings
            }
          ]"
          color="primary"
          data-test-id="add-approver"
          variant="outlined"
          @click="addApproverField"
        >
          {{ i18n.t('buttons.add_approver') }}
        </v-btn>
        <!-- /ADD APPROVER -->
      </div>
    </InviteeSettings>
  </div>
</template>
<script setup lang="ts">
import UnorderedInvitees from '@/components/create/UnorderedInvitees.vue';
import OrderedInvitees from '@/components/create/OrderedInvitees.vue';
import InviteeSettings from '@/components/create/settings/InviteeSettings.vue';
import { useCreateStore } from '@/stores/create.ts';
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { SigneeType } from '@/types/enums/SigneeType.ts';
import { useI18n } from 'vue-i18n';
import type { Signee } from '@/types/Signee.ts';
import { useEmitter } from '@/composables/useEmitter.ts';
import { cloneDeep, debounce } from '@/composables/useUtils.ts';
import type { AddSignee } from '@/types/AddSignee.ts';
import { useExpertSettings } from '@/composables/useExpertSettings.ts';
import { useDisplay } from 'vuetify/framework';
import {
  injectStrict,
  PlaceHolderComposableKey
} from '@/composables/placement/placementSymbols.ts';
import {
  defaultPosition,
  defaultResizeDimensions
} from '@/composables/placement/usePlacement.ts';
import { PlaceholderType } from '@/types/enums/PlaceholderType.ts';
import { persistSigneePlaceholders } from '@/composables/placement/usePlacementUtility.ts';
import { buildFileAutographPositionsFromSignings } from '@/utils/signeeSignings.ts';
import { useGlobalStore } from '@/stores/global.ts';

defineProps({
  hideTitle: {
    type: Boolean,
    default: false
  },
  layoutStableExpert: {
    type: Boolean,
    default: false
  }
});

const i18n = useI18n();
const router = useRouter();
const createStore = useCreateStore();
const globalStore = useGlobalStore();
const emitter = useEmitter();
const display = useDisplay();
const { expertSettings } = useExpertSettings();

const signaturesForm = ref();
const dragging = ref(false);
const currentFieldPreviousValue = ref();

const {
  placePlaceholdersAutomatically,
  placeholderTemplateRefs,
  resetPlaceholderState
} = injectStrict(PlaceHolderComposableKey);

const documentId = computed(() => {
  if (Array.isArray(router.currentRoute.value.params.document))
    return router.currentRoute.value.params.document[0];
  return router.currentRoute.value.params.document;
});

async function addSigneeField() {
  const payload = {
    signOrder: createStore.nextGroupIndex
  };
  // Capture before add: new signees clear per-file completeness and the
  // PlaceSignaturesSwitch watcher sets placeSignaturesModel false.
  //
  // While the signing-workflow dialog defers persistence the new invitee only
  // exists in the local draft, so placing a placeholder for it here would draw
  // it on the PDF behind the still-open dialog — before the user confirmed
  // anything (and it would linger until the draft is dropped on Cancel).
  // useSigningWorkflow.confirm() auto-places the signees it actually created,
  // gated on the intent snapshot taken when the dialog opened.
  const shouldAutoPlace =
    createStore.placeSignaturesModel && !createStore.deferPersistence;
  const response = await createStore.addInvitee(documentId.value, payload);
  const invitee = response.data;
  createStore.addInviteeToLocalData(invitee);
  signaturesForm.value.resetValidation();
  if (display.lgAndUp.value) {
    await createStore.focusInputField(invitee.signeeId);
  }
  if (shouldAutoPlace) {
    if (display.mdAndDown.value) {
      globalStore.drawer = false;
      await nextTick();
    }
    const placeholdersToUpdate = await placePlaceholdersAutomatically(null);
    await persistSigneePlaceholders(
      createStore,
      placeholdersToUpdate,
      placeholderTemplateRefs?.value,
      createStore.filteredSignees,
      createStore.document
    );
  }
}

async function addApproverField() {
  const payload: Partial<AddSignee> = {
    signOrder: 0,
    signeeType: SigneeType.APPROVE
  };
  const response = await createStore.addInvitee(documentId.value, payload);
  await moveApproverToFirstPlace(response.data);
  if (display.lgAndUp.value) {
    await createStore.focusInputField(response.data.signeeId);
  }
  signaturesForm.value.resetValidation();
}

async function moveApproverToFirstPlace(approver: Signee) {
  const tempGroups = cloneDeep(createStore.signeesOrdered);
  // change the group order to have the approver as first item
  if (createStore.ordered) {
    tempGroups.splice(0, 0, [approver]);
    createStore.signeesOrdered =
      createStore.filterEmptyInBetweenGroups(tempGroups);
  } else {
    tempGroups.splice(0, 0, [approver]);
    await nextTick(async () => {
      await createStore.onOrderSwitchToggle(
        true,
        true,
        tempGroups,
        approver.signeeId
      );
    });
  }
}

async function removeInviteeFromDocument(invitee: Signee) {
  // The X button only renders inside the signing-workflow dialog, where every
  // edit is deferred and persisted in one batch on Confirm. Remove the signee
  // from the local draft directly — filter it out and renormalize the order
  // through the purely-local updateSignOrderInLocalData. We deliberately do NOT
  // call removeInviteeFromLocalData here: it still issues a documentsSignOrder
  // update API call (guarded only by deferPersistence), which must never fire
  // from the dialog. reconcileParticipants() issues the DELETE for an
  // already-persisted signee on Confirm; a draft-only signee just disappears.
  const remainingGroups = createStore.signeesOrdered
    .map((group) =>
      group.filter((signee: Signee) => signee.signeeId !== invitee.signeeId)
    )
    .filter((group) => group.length > 0);
  createStore.updateSignOrderInLocalData(remainingGroups);

  resetPlaceholderState(
    {
      id: invitee.signeeId,
      placeHolderType: PlaceholderType.SIGNATURE
    },
    defaultPosition,
    defaultResizeDimensions
  );

  //add signee field if no fields are around
  if (!createStore.hasInvitees) {
    await addSigneeField();
  }
  signaturesForm.value.resetValidation();
}

function onBlur(signee: Signee) {
  if (!signee) return;
  const isValidEmail = createStore.validateEmail(signee.email);
  emitter.$emit('signee-blur', { ...signee, isValidEmail });
}

function onFocus(signee: Signee) {
  if (!signee) return;
  emitter.$emit('signee-focus', signee);
  currentFieldPreviousValue.value = { ...signee };
}

const handleSigneeDebounced = debounce(function (context) {
  const { event, signee } = context;
  handleSignee(event, signee);
}, 700);

async function handleSignee(_, signee: Signee) {
  if (!signee) return;

  try {
    const isValidEmail = createStore.validateEmail(signee.email);
    if (!isValidEmail && signee.email) {
      return;
    }

    if (signee && signee.signeeId && documentId.value) {
      await createStore.updateInvitee(documentId.value, signee.signeeId, {
        email: signee.email === '' ? null : signee.email,
        ...(signee.policy?.canModifyAutographPosition && {
          autographPosition: signee.autographPosition,
          fileAutographPositions:
            buildFileAutographPositionsFromSignings(signee.signings) ?? {}
        }),
        signOrder: signee.signOrder
      });

      currentFieldPreviousValue.value = { ...signee };
      if (createStore.placeSignaturesModel) {
        emitter.$emit('signee-blur', { ...signee, isValidEmail });
      } else {
        emitter.$emit('signee-blur', signee);
      }
    }
  } catch (error) {
    currentFieldPreviousValue.value = { ...signee };
    console.error('Error handling signee (add / update)', error);
  }
}

onMounted(async () => {
  if (!createStore.hasInvitees) {
    //this delay is needed for the public access case where the created document has no signees
    setTimeout(async () => {
      // Re-check: the document (and with it the draft) can still be loading when
      // the dialog opens, so the signees may have arrived in the meantime —
      // appending a field then would add an empty one next to them.
      if (!createStore.hasInvitees) {
        await addSigneeField();
      }
      createStore.inviteeFormTemplateRef = signaturesForm.value;
    }, 600);
  }
  //this delay is needed for the public erp access
  setTimeout(async () => {
    if (!createStore?.inviteeFormTemplateRef) {
      createStore.inviteeFormTemplateRef = signaturesForm.value;
    }
  }, 600);
});

onUnmounted(() => {
  createStore.inviteeFormTemplateRef = null;
});
</script>

<style scoped lang="scss">
.add-approver-button {
  border: none !important;
}

.is-group :deep(.signee-group-card) {
  margin-left: 20px;
}
</style>
